import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { ChatState } from 'sdk';

import { useSDK } from '../../app';
import { styled } from '../../styled';
import { getQueryStringValue } from '../../utils/getQueryStringValue';
import { isMobile } from '../../utils/isMobile';
import { VIDEO_CALLS_ARE_SUPPORTED } from '../../utils/isVideoCallsSupported';
import { AngleDownIcon } from '../chat/chatHeader/angleDownIcon';
import { DotsIcon } from '../chat/chatHeader/dotsIcon';
import { HeaderSelect } from '../chat/chatHeader/headerSelect';
import { ChatHeaderVideoIcon } from '../chat/chatHeader/videoIcon';

export const IS_WEBVIEW = getQueryStringValue('webview');
const Component = ({
	className,
	onCloseClick,
	disabledActionIcons = false,
	showAngle = isMobile && !IS_WEBVIEW,
}: {
	className: string;
	showAngle?: boolean;
	onCloseClick?: VoidFunction;
	disabledActionIcons?: boolean;
}): ReactElement => {
	const [showSelect, setShowSelect] = useState(false);
	const { welcomeDataStore, conversationsStore, uiStateStore, showcaseStatusStore } = useSDK();
	const operatorAvailable = welcomeDataStore.data?.showcase.isOnline || !!conversationsStore.currentDialog;

	const showActionIcons =
		VIDEO_CALLS_ARE_SUPPORTED && operatorAvailable && !showcaseStatusStore.blocked && !disabledActionIcons;

	const showVideoIcon =
		welcomeDataStore.data?.showcase.useVideo &&
		((welcomeDataStore.data?.showcase.startWithVideo && welcomeDataStore.data.showcase.isOnline) ||
			([ChatState.prechat, ChatState.chat].includes(uiStateStore.chatState) &&
				conversationsStore.currentDialog?.wasVideoEnabled)) &&
		showActionIcons;

	const cobrowsingEnabled =
		welcomeDataStore.data?.showcase.useCobrowsing &&
		conversationsStore.isCurrentDialogDistributed &&
		!isMobile &&
		showActionIcons;

	const showDots =
		uiStateStore.chatState === ChatState.chat &&
		uiStateStore.opened &&
		(cobrowsingEnabled || welcomeDataStore.data?.showcase.useSendHistory);

	useEffect(() => {
		if (showSelect) {
			const f = (): void => setShowSelect(false);
			document.addEventListener('click', f);
			return (): void => document.removeEventListener('click', f);
		}
	}, [showSelect]);

	return (
		<div className={className}>
			{showVideoIcon && <ChatHeaderVideoIcon withMarginRight={showDots || showAngle || showSelect} />}
			{showDots && <DotsIcon withMarginRight={showAngle} onClick={(): void => setShowSelect(!showSelect)} />}
			{showAngle && <AngleDownIcon onClick={onCloseClick || ((): void => uiStateStore.setOpened(false))} />}
			{showSelect && !uiStateStore.showRating && (
				<HeaderSelect
					showCobrowsingOption={!!cobrowsingEnabled && !!welcomeDataStore.data?.showcase.isOnline}
					showSendToEmailOption={!!welcomeDataStore.data?.showcase.useSendHistory}
				/>
			)}
		</div>
	);
};

export const HeaderIconsContainer = styled(observer(Component))(css`
	align-items: center;
	display: flex;
`);
